import {
  Button, Flex, Icon, IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent, ModalFooter,
  ModalHeader,
  ModalOverlay, Text, Tooltip, useToast
} from "@chakra-ui/react";
import * as React from "react";
import { FormInput } from "../../../components/Forms";
import { QUESTIONNAIRE_STATUSES, showErrorToast, showSuccessToast } from "../../../utils";
import { MdAttachFile } from "react-icons/md";
import { FaCopy, FaDownload } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { CSRDKeys, useAttachCSRDFile, useGetCSRDFiles } from "../../../hooks/query/csrd";
import { useGetAttachment } from "../../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";

function AttachedFilesModal({ isOpen, onClose, questionnaireData, isOpenedInModal }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "csrd.questionnaire"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const dispatch = useDispatch();
  const toast = useToast();
  const queryClient = useQueryClient();

  const uploadBtnId = "uploadFileButton";

  const {
    data: csrdFiles
  } = useGetCSRDFiles({ type: questionnaireData?.type ?? "", year: questionnaireData?.year ?? "" }, {
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
    refetchOnWindowFocus: false,
    enabled: questionnaireData !== null && typeof questionnaireData !== "undefined"
  });

  const getConfig = (onSuccessMessage = "") => {
    return {
      onMutate: () => dispatch(showAppSpinner()),
      onSuccess: () => {
        if (onSuccessMessage !== "") {
          showSuccessToast(toast, onSuccessMessage);
        }
        queryClient.invalidateQueries(CSRDKeys.CSRDFiles());
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      onSettled: () => dispatch(hideAppSpinner()),
      cacheTime: 0
    };
  };

  const { mutate: uploadFile } = useAttachCSRDFile(getConfig(t("attachmentUploadedMsg")));
  const { mutate: downloadAttachment } = useGetAttachment(getConfig());

  const handleFileUpload = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      const formData = new FormData();
      formData.append("file", e.target.files[0]);
      document.getElementById(uploadBtnId).value = null;
      uploadFile({
        data: formData,
        type: questionnaireData?.type ?? "",
        year: questionnaireData?.year ?? ""
      });
    }
  };

  const handleUploadFileClick = () => {
    const uploadBtn = document.getElementById(uploadBtnId);
    if (uploadBtn) {
      uploadBtn.click();
    }
  };

  return isOpen ?
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t("attachedFiles")}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Flex flexFlow="column" gap={2} borderRadius={"4"} marginBlock={4} border={"1px solid lightgray"} padding={4}>
            <Flex alignItems="center" gap={2}>
              <FormInput
                id={uploadBtnId}
                name={uploadBtnId}
                type="file"
                onChange={(e) => handleFileUpload(e)}
                paddingInlineStart="0"
                border="none"
                accept={"image/*, .xlsx, .xls, .doc, .docx, .ppt, .pptx, .txt, .pdf"}
                controlProps={{ display: "none" }}
              />
              <Tooltip
                label={t("attachFileButtonTooltip")}
                whiteSpace={"pre-line"}
                hasArrow
              >
                <Button
                  colorScheme="white"
                  color="esg_lightgreen"
                  border={"1px solid"}
                  borderColor={"esg_lightgreen"}
                  fontSize={"md"}
                  onClick={!isOpenedInModal && questionnaireData.status !== QUESTIONNAIRE_STATUSES.FINALIZED ?
                    handleUploadFileClick : () => {
                    }}
                  marginRight={3}
                  isDisabled={isOpenedInModal || questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED}
                >
                  <Icon
                    as={MdAttachFile}
                    inlineSize="5"
                    blockSize="5"
                    color="esg_green"
                    marginEnd="2"
                  />
                  {t("attachNewFileButton")}
                </Button>
              </Tooltip>

            </Flex>

            {csrdFiles && csrdFiles.length > 0 ?
              <Flex gap={2} overflow={"auto"}>
                {csrdFiles.map(file => {
                  return (
                    <Flex maxWidth={"200px"} borderRadius={"4"} flexFlow={"column"} alignItems={"center"}
                          key={file.documentId}
                          border={"1px solid lightgray"} padding={2}>
                      <Flex gap={1}>
                        <Tooltip
                          label={t("downloadFileTooltip")}
                          whiteSpace={"pre-line"}
                          hasArrow
                        >
                          <IconButton
                            aria-label="download-data-source-file"
                            type="button"
                            icon={<FaDownload size="20" color="green" />}
                            flexShrink="0"
                            size="sm"
                            tabIndex={-1}
                            onClick={() => {
                              downloadAttachment({ attachmentId: file?.documentId });
                            }}
                          />
                        </Tooltip>
                        <Tooltip
                          label={t("copyFileNameTooltip")}
                          whiteSpace={"pre-line"}
                          hasArrow
                        >
                          <IconButton
                            aria-label="copy-file-name"
                            type="button"
                            icon={<FaCopy size="20" color="#b9b90f" />}
                            flexShrink="0"
                            size="sm"
                            tabIndex={-1}
                            onClick={() => {
                              navigator.clipboard.writeText(file.name);
                              showSuccessToast(toast, t("fileNameCopied"));
                            }}
                          />
                        </Tooltip>
                      </Flex>
                      <Text
                        textOverflow="ellipsis"
                        overflow="hidden"
                        whiteSpace="nowrap" width={"100%"} title={file.name}
                      >
                        {file.name}
                      </Text>
                    </Flex>
                  );
                })}
              </Flex>
              : t("noFilesAttached")}
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {globalTranslation("buttons.close")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal> : null;
}

export default AttachedFilesModal;